import type { AlertTone, DashboardAlerts } from '@qlk/shared';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { DanhSachSkeleton, EmptyState, Nut } from '../../../ui/index.js';

/**
 * Khung "Cần xử lý": **danh sách việc bấm được**, mỗi dòng một chấm màu theo
 * mức độ, tiêu đề việc và một dòng phụ, bấm là sang đúng trang xử lý.
 *
 * Bản cũ cũng là danh sách nhưng chỉ **tiêu đề** mới bấm được, nên vùng bấm
 * thật hẹp hơn vùng trông như bấm được. Nay cả dòng là một `Link`.
 */

/* Chấm 8px: lấy token, không gõ mã màu — xem `theme.css`. */
export const MAU_CHAM: Record<AlertTone, string> = {
  bad: 'var(--color-giam)',
  warn: 'var(--color-warn-chu)',
  info: 'var(--color-nhan)',
};

export interface KhungCanXuLyProps {
  duLieu: DashboardAlerts | undefined;
  dangTai: boolean;
  loi?: string | null;
  onThuLai?: () => void;
  /** Số dòng hiện trong khung. Xem hết thì bấm sang `/canh-bao`. */
  toiDa?: number;
}

export function KhungCanXuLy({ duLieu, dangTai, loi, onThuLai, toiDa = 5 }: KhungCanXuLyProps) {
  const items = duLieu?.items ?? [];
  const hien = items.slice(0, toiDa);

  return (
    <section
      data-khoi="can-xu-ly"
      className="flex flex-col rounded-the border border-vien bg-be-mat"
    >
      <div className="flex items-center justify-between gap-3 border-b border-vien-trong px-5 py-3.5">
        <h2 className="text-tieu-de-khoi font-semibold text-chu">Cần xử lý</h2>
        {!dangTai && !loi && items.length > 0 && (
          <span className="so-lieu rounded-chip bg-neutral-nen px-2 py-0.5 text-phu font-semibold text-neutral-chu">
            {items.length} việc
          </span>
        )}
      </div>

      {dangTai ? (
        <div className="p-5">
          <DanhSachSkeleton so={5} cao="2rem" />
        </div>
      ) : loi ? (
        <EmptyState
          loi
          title="Không tải được cảnh báo"
          description={loi}
          {...(onThuLai
            ? {
                action: (
                  <Nut kieu="phu" onClick={onThuLai}>
                    Thử lại
                  </Nut>
                ),
              }
            : {})}
        />
      ) : items.length === 0 ? (
        /* Không có việc nào là **một dòng**, không phải một khung rỗng to:
           trạng thái tốt thì không cần chiếm chỗ. */
        <p className="px-5 py-4 text-co-ban text-chu-phu">Mọi thứ đang ổn.</p>
      ) : (
        <ul className="flex flex-col">
          {hien.map((c) => (
            <li key={c.id} className="border-b border-vien-trong last:border-b-0">
              <Link
                to={c.link}
                className="flex items-start gap-3 px-5 py-3 transition-colors hover:bg-header-bang"
              >
                <span
                  className="mt-1.5 inline-block size-2 shrink-0 rounded-full"
                  style={{ backgroundColor: MAU_CHAM[c.tone] }}
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-co-ban font-semibold text-chu">
                    {c.title}
                  </span>
                  <span className="block truncate text-phu text-chu-phu">{c.text}</span>
                </span>
                <ChevronRight size={16} aria-hidden="true" className="mt-1 shrink-0 text-chu-phu" />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {duLieu && duLieu.unavailable.length > 0 && (
        <p className="mx-5 my-3 rounded-chip bg-warn-nen px-3 py-2 text-phu text-warn-chu">
          Chưa lấy được cảnh báo từ: {duLieu.unavailable.join(', ')}
        </p>
      )}

      {items.length > 0 && (
        <Link
          to="/canh-bao"
          className="vung-bam mt-auto inline-flex items-center border-t border-vien-trong px-5 text-co-ban font-semibold text-nhan hover:text-nhan-dam"
        >
          Xem tất cả cảnh báo →
        </Link>
      )}
    </section>
  );
}
